import { IApiClient, getApiClient } from "./ApiClient/ApiClient";
import logger, { LoggingSource } from "@/shared/Logger";
import AdministrationPayer, {
  AdministrationPayer_Request,
} from "@/models/APIModels/AdministrationPayer";
import IResponseModel from "./ResponseModel";
import PaymentSource from "@/models/APIModels/PaymentSource";
import ChargeDetail from "@/models/APIModels/ChargeDetail";
import IncomingPaymentTransactionList from "@/models/APIModels/IncomingPaymentTransactionList";
import CMPayment from "@/models/APIModels/CMPayment";
import { User } from "@/models";
import { UserSecurityDetail } from "@/models/APIModels/UserSecurityDetail";
export default class CommitteeMemberApi {
  private readonly _apiClient: IApiClient;
  private readonly BASE_URL = "/committeemember";

  constructor(apiClient: IApiClient = getApiClient()) {
    this._apiClient = apiClient;
  }

  async insert(committeeMember: AdministrationPayer_Request): Promise<
    IResponseModel<{
      accessToken: string;
      refreshToken: string;
      user: UserSecurityDetail;
    }>
  > {
    try {
      logger.info("InsertCommitteeMember...", LoggingSource.UIUser);

      logger.debug(
        `Parameter CommitteMember: ${JSON.stringify(committeeMember)}`,
        LoggingSource.UIUser,
      );

      logger.timeStart("InsertCommitteeMember call", LoggingSource.UIUser);
      const result = await this._apiClient.post<
        AdministrationPayer_Request,
        {
          accessToken: string;
          refreshToken: string;
          user: UserSecurityDetail;
        }
      >(this.BASE_URL, committeeMember);
      logger.timeEnd("InsertCommitteeMember call", LoggingSource.UIUser);

      logger.debug(
        `InsertCommitteeMember response: ${JSON.stringify(result)}`,
        LoggingSource.UIUser,
      );
      return result;
    } catch (error) {
      logger.timeEnd("InsertCommitteeMember call", LoggingSource.UIUser);
      logger.error(JSON.stringify(error), LoggingSource.UIUser);

      throw error;
    } finally {
      logger.info("...InsertCommitteeMember", LoggingSource.UIUser);
    }
  }

  async update(
    committeeMember: AdministrationPayer_Request,
    schoolOfficerReference: string,
  ): Promise<IResponseModel<User>> {
    try {
      logger.info("UpdateCommitteeMember...", LoggingSource.UIUser);

      logger.debug(
        `Parameter CommitteMember: ${JSON.stringify(
          committeeMember,
        )}, School Officer Reference: ${schoolOfficerReference}`,
        LoggingSource.UIUser,
      );

      logger.timeStart("UpdateCommitteeMember call", LoggingSource.UIUser);
      const result = await this._apiClient.put<
        AdministrationPayer_Request,
        User
      >(`${this.BASE_URL}/${schoolOfficerReference}}`, committeeMember);
      logger.timeEnd("UpdateCommitteeMember call", LoggingSource.UIUser);

      logger.debug(
        `UpdateCommitteeMember response: ${JSON.stringify(result)}`,
        LoggingSource.UIUser,
      );
      return result;
    } catch (error) {
      logger.timeEnd("UpdateCommitteeMember call", LoggingSource.UIUser);
      logger.error(JSON.stringify(error), LoggingSource.UIUser);

      throw error;
    } finally {
      logger.info("...UpdateCommitteeMember", LoggingSource.UIUser);
    }
  }

  async setPaymentSource(
    paymentSource: PaymentSource,
    schoolOfficerReference: string,
    concurrencyRV: string,
  ): Promise<IResponseModel<AdministrationPayer>> {
    try {
      logger.info("setPaymentSource...", LoggingSource.UIUser);

      logger.debug(
        `Parameter PaymentSource: ${JSON.stringify(
          paymentSource,
        )}, School Officer Reference: ${schoolOfficerReference}, ConcurrencyRV: ${concurrencyRV}`,
        LoggingSource.UIUser,
      );

      logger.timeStart("setPaymentSource call", LoggingSource.UIUser);
      const result = await this._apiClient.put<
        { paymentSource: PaymentSource; concurrencyRV: string },
        AdministrationPayer
      >(`${this.BASE_URL}/${schoolOfficerReference}/paymentsource`, {
        paymentSource,
        concurrencyRV,
      });
      logger.timeEnd("setPaymentSource call", LoggingSource.UIUser);

      logger.debug(
        `setPaymentSource response: ${JSON.stringify(result)}`,
        LoggingSource.UIUser,
      );
      return result;
    } catch (error) {
      logger.timeEnd("setPaymentSource call", LoggingSource.UIUser);
      logger.error(JSON.stringify(error), LoggingSource.UIUser);

      throw error;
    } finally {
      logger.info("...setPaymentSource", LoggingSource.UIUser);
    }
  }

  async selectByUserReference(
    schoolOfficerReference: string,
  ): Promise<IResponseModel<AdministrationPayer>> {
    try {
      logger.info("SelectByUserReference...", LoggingSource.UIUser);

      logger.debug(
        `Parameter SchoolOfficerReference: ${schoolOfficerReference}`,
        LoggingSource.UIUser,
      );

      logger.timeStart("SelectByUserReference call", LoggingSource.UIUser);
      const result = await this._apiClient.get<AdministrationPayer>(
        `${this.BASE_URL}/${schoolOfficerReference}}`,
      );
      logger.timeEnd("SelectByUserReference call", LoggingSource.UIUser);

      logger.debug(
        `SelectByUserReference response: ${JSON.stringify(result)}`,
        LoggingSource.UIUser,
      );
      return result;
    } catch (error) {
      logger.timeEnd("SelectByUserReference call", LoggingSource.UIUser);
      logger.error(JSON.stringify(error), LoggingSource.UIUser);

      throw error;
    } finally {
      logger.info("...SelectByUserReference", LoggingSource.UIUser);
    }
  }

  async chargeAccount(
    userReference: string,
  ): Promise<IResponseModel<ChargeDetail>> {
    try {
      logger.info("ChargeAccount...", LoggingSource.UIUser);

      logger.debug(
        `Parameter UserReference: ${userReference}`,
        LoggingSource.UIUser,
      );

      logger.timeStart("ChargeAccount call", LoggingSource.UIUser);
      const result = await this._apiClient.get<ChargeDetail>(
        `${this.BASE_URL}/${userReference}}/chargeaccount`,
      );
      logger.timeEnd("ChargeAccount call", LoggingSource.UIUser);

      logger.debug(
        `ChargeAccount response: ${JSON.stringify(result)}`,
        LoggingSource.UIUser,
      );
      return result;
    } catch (error) {
      logger.timeEnd("ChargeAccount call", LoggingSource.UIUser);
      logger.error(JSON.stringify(error), LoggingSource.UIUser);

      throw error;
    } finally {
      logger.info("...ChargeAccount", LoggingSource.UIUser);
    }
  }

  async getTransactionsList(
    queryString: string,
  ): Promise<IResponseModel<IncomingPaymentTransactionList>> {
    try {
      logger.info("getTransactionsList...", LoggingSource.UIDonorPayment);

      logger.timeStart(
        "getTransactionsList call",
        LoggingSource.UIDonorPayment,
      );

      const result = await this._apiClient.get<IncomingPaymentTransactionList>(
        `${this.BASE_URL}/transactions/list?${queryString}`,
      );

      logger.timeEnd("getTransactionsList call", LoggingSource.UIDonorPayment);

      logger.debug(
        `getTransactionsList response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonorPayment,
      );
      return result;
    } catch (error) {
      logger.timeEnd("getTransactionsList call", LoggingSource.UIDonorPayment);
      logger.error(JSON.stringify(error), LoggingSource.UIDonorPayment);
      throw error;
    } finally {
      logger.info("...getTransactionsList", LoggingSource.UIDonorPayment);
    }
  }

  async getPaymentList(queryString: string) {
    try {
      logger.info("getList...", LoggingSource.UIDonorPayment);

      logger.timeStart("getList call", LoggingSource.UIDonorPayment);

      const result = await this._apiClient.get<CMPayment[]>(
        `${this.BASE_URL}/paymentlist?${queryString}`,
      );

      logger.timeEnd("getList call", LoggingSource.UIDonorPayment);

      logger.debug(
        `getList response: ${JSON.stringify(result)}`,
        LoggingSource.UIDonorPayment,
      );
      return result;
    } catch (error) {
      logger.timeEnd("getList call", LoggingSource.UIDonorPayment);
      logger.error(JSON.stringify(error), LoggingSource.UIDonorPayment);
      throw error;
    } finally {
      logger.info("...getList", LoggingSource.UIDonorPayment);
    }
  }
}
